import { ColumnDef } from "@tanstack/react-table";
import { t } from "i18next";
import moment from "moment";
import { useMemo } from "react";
import { ItemsTable } from "ui";

import { AuditAlertResponse } from "../../auditLog.types";
import { buildAlertSummary, isDigestContext } from "../../helpers";
import { SeverityBadge } from "../SeverityBadge";
import { AuditAlertsTableProps } from "./AuditAlertsTable.types";

import styles from "./AuditAlertsTable.module.scss";

const AuditAlertsTable = ({
  auditAlerts,
  isLoading,
  sorting,
  setSorting
}: AuditAlertsTableProps) => {
  const columns: Array<ColumnDef<AuditAlertResponse, string>> = useMemo(
    () => [
      {
        id: "severity",
        accessorKey: "severity",
        header: t("Severity"),
        size: 12,
        minSize: 12,
        enableSorting: false,
        cell: ({ row }) => (
          <SeverityBadge severity={auditAlerts[row.index].severity} />
        )
      },
      {
        id: "summary",
        accessorKey: "summary",
        header: t("Summary"),
        size: 40,
        minSize: 40,
        enableSorting: false,
        cell: ({ row }) => (
          <span className={styles.summary}>
            {buildAlertSummary(auditAlerts[row.index])}
          </span>
        )
      },
      {
        id: "kind",
        accessorKey: "id",
        header: t("Type"),
        size: 14,
        minSize: 14,
        enableSorting: false,
        cell: ({ row }) => {
          const digest = isDigestContext(auditAlerts[row.index].context);

          return (
            <span
              className={
                digest ? styles["badge--digest"] : styles["badge--immediate"]
              }
            >
              {digest ? t("Digest") : t("Immediate")}
            </span>
          );
        }
      },
      {
        id: "status",
        accessorKey: "isAcknowledged",
        header: t("Status"),
        size: 18,
        minSize: 18,
        enableSorting: false,
        cell: ({ row }) => {
          const alert = auditAlerts[row.index];

          return alert.isAcknowledged ? (
            <span className={styles.status__reviewed}>{t("Reviewed")}</span>
          ) : (
            <span className={styles.status__pending}>{t("Unreviewed")}</span>
          );
        }
      },
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: t("Date & time"),
        size: 16,
        minSize: 16,
        cell: ({ row }) => (
          <span className={styles.nowrap}>
            {moment(auditAlerts[row.index].createdAt).format(
              "DD/MM/YYYY HH:mm"
            )}
          </span>
        )
      }
    ],
    [auditAlerts]
  );

  return (
    <ItemsTable
      columns={columns}
      data={auditAlerts}
      sorting={sorting}
      setSorting={setSorting}
      isLoading={isLoading}
      skeletonHeight={56}
      emptyListMessage={t("No alerts to review")}
      rowsClassName={styles.tallRow}
      getLinkUrl={({ id }) => `/audit-alerts/${id}`}
    />
  );
};

export default AuditAlertsTable;
